import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  MenuItem,
  OutlinedInput,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Typography,
  Button,
} from "@mui/material";
import SimpleBar from "simplebar-react";
import { COUNTRIES_WITH_STATES } from "../../../constants";
import {
  fetchRoutePartners,
  getTransitDayOptions,
  getShippingServiceOptions,
  fetchShippingWarehouses,
  createShippingService,
} from "./shippingService.helper";
import { useSelector, useDispatch } from "react-redux";
import {
  setShippingServiceState,
  resetShippingServiceState,
} from "../../../store/shippingServiceSlice";
import { useFormik } from 'formik';
import * as Yup from 'yup';

const LocationCard = ({ location, isSelected, onSelect }) => {
  return (
    <Box
      sx={{
        background: "#fff",
        borderRadius: "8px",
        boxShadow: "0 2px 8px 0 rgba(60,60,60,0.08)",
        padding: "20px",
        cursor: "pointer",
        minHeight: "140px",
        transition: "box-shadow 0.2s cubic-bezier(.4,0,.2,1)",
        textAlign: "left",
        border: isSelected ? "2px solid #27AE60" : "1px solid #E5E5E8",
        position: "relative",
        '&:hover': {
          boxShadow: "0 4px 16px 0 rgba(60,60,60,0.13)",
        },
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
      }}
      onClick={() => onSelect()}
    >
      {isSelected && (
        <>
          {/* Green triangle */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: 0,
              height: 0,
              borderTop: "48px solid #27AE60",
              borderLeft: "48px solid transparent",
              zIndex: 2,
              borderTopRightRadius: "8px",
            }}
          />
          {/* SVG Checkmark centered in triangle */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              width: "48px",
              height: "48px",
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "flex-end",
              zIndex: 3,
              pointerEvents: "none",
              userSelect: "none",
            }}
          >
            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg" style={{marginTop: 8, marginRight: 6}}>
              <path d="M5 12.5L10 17.5L17 7.5" stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </Box>
        </>
      )}
      <Typography variant="h6" sx={{ fontWeight: 700, marginBottom: "8px" }}>
        {location.warehouseName}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "4px" }}>
        {location.addressLine1}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ marginBottom: "4px" }}>
        {location.city + ", " + location.state}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {location.country}
      </Typography>
    </Box>
  );
};

export default function ShippingWarehouseStep({ onBack, onNext, loading }) {
  // Local state for lists
  const [routePartners, setRoutePartners] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);
  const [selectedLocationId, setSelectedLocationId] = useState(null);
  const [transitDayOptions] = useState(getTransitDayOptions());
  const [shippingServiceOptions] = useState(getShippingServiceOptions());
  const [warehouses, setWarehouses] = useState([]);

  // Redux state for selected values
  const shippingServiceData = useSelector((state) => state.shippingService.shippingServiceData);
  const {
    routePartner,
    selectedCountry,
    selectedState,
    transitDayRange,
    shippingServiceType,
    wareHouseDetails
  } = shippingServiceData;
  const dispatch = useDispatch();

  // Restore useMemo for fromCountry
  const fromCountry = React.useMemo(() => {
    const partner = routePartners.find((p) => p.id === routePartner?.id);
    return partner?.country || '';
  }, [routePartners, routePartner]);

  // Formik initial values
  const initialValues = {
    routePartner: routePartner || { id: '', name: '' },
    fromCountry: fromCountry || '',
    toCountry: selectedCountry || '',
    toState: selectedState || '',
    shippingServiceType: shippingServiceType || '',
    transitDayRange: transitDayRange || '',
    wareHouseDetails: wareHouseDetails || { id: '', warehouseName: '' },
  };

  // Formik validation schema
  const validationSchema = Yup.object({
    routePartner: Yup.object({
      id: Yup.string().required('Route Partner is required'),
      name: Yup.string().required('Route Partner Name is required'),
    }),
    fromCountry: Yup.string().required('From Country is required'),
    toCountry: Yup.string().required('To Country is required'),
    toState: Yup.string().required('To State is required'),
    shippingServiceType: Yup.string().required('Shipping Service Type is required'),
    transitDayRange: Yup.string().required('Transit Day Range is required'),
    wareHouseDetails: Yup.object({
      id: Yup.string().required('Warehouse is required'),
      warehouseName: Yup.string().required('Warehouse Name is required'),
    }),
  });

  // useFormik hook
  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        const response = await createShippingService({
          ...values,
          id: shippingServiceData.id, // Pass id for edit mode
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        });
        saveShippingStepToRedux({ ...response, id: response.id });
        setSubmitting(false);
        resetForm();
        if (onNext) onNext(); // Move to next step
      } catch (error) {
        setSubmitting(false);
        // Optionally show an error message
      }
    },
  });

  // Now define functions that use formik
  const handleSelect = (id) => {
    setSelectedLocationId(id);
  };

  const handleTransitDayChange = (event) => {
    dispatch(setShippingServiceState({ transitDayRange: event.target.value }));
  };

  // Remove handleRoutePartnerChange and any reference to selectedRoutePartner

  const handleCountryChange = (event) => {
    const countryName = event.target.value;
    // dispatch(setShippingServiceState({ selectedCountry: countryCode, selectedState: "" })); // Reset state when country changes
    // Get states for selected country
    console.log(countryName, "country states");
    formik.setFieldValue('toCountry', countryName);
    if (countryName && availableStates.length === 0) {
      const country = COUNTRIES_WITH_STATES.find((c) => c.name === countryName);
      setAvailableStates(country ? country.states : []);
    }
  };

  const handleStateChange = (event) => {
    dispatch(setShippingServiceState({ selectedState: event.target.value }));
  };

  const handleShippingServiceTypeChange = (event) => {
    dispatch(setShippingServiceState({ shippingServiceType: event.target.value }));
  };

  // Fetch route partners on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch route partners
        const partners = await fetchRoutePartners();
        setRoutePartners(partners);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Update availableStates when toCountry changes (use Formik value for reactivity)
  useEffect(() => {
    if (formik.values.toCountry) {
      const country = COUNTRIES_WITH_STATES.find((c) => c.name === formik.values.toCountry);
      setAvailableStates(country ? country.states : []);
    } else {
      setAvailableStates([]);
    }
  }, [formik.values.toCountry]);

  const saveShippingStepToRedux = (values) => {
    dispatch(setShippingServiceState({
      id: values.id,
      routePartner: values.routePartner,
      selectedCountry: values.toCountry,
      selectedState: values.toState,
      transitDayRange: values.transitDayRange,
      shippingServiceType: values.shippingServiceType,
      wareHouseDetails: values.wareHouseDetails,
      createdAt: values.createdAt,
      updatedAt: values.updatedAt,
    }));
    // You can add more dispatches here if you add more fields to Redux
  };

  // Fetch warehouses when Formik routePartnerId changes
  useEffect(() => {
    const fetchWarehouses = async () => {
      if (formik.values.routePartner?.id) {
        const data = await fetchShippingWarehouses({ routePartnerId: formik.values.routePartner.id });
        setWarehouses(data);
      } else {
        setWarehouses([]);
      }
    };
    fetchWarehouses();
  }, [formik.values.routePartner]);

  if (loading) {
    return <Box sx={{ p: 4, textAlign: 'center' }}>Loading...</Box>;
  }

  return (
    <form onSubmit={formik.handleSubmit}>
      <SimpleBar
        forceVisible="x"
        autoHide={true}
        style={{ overflowX: "auto", maxHeight: "calc(100vh - 356px)" }}
        className="custom-scrollbar"
      >
        <Box sx={{ width: "50%", margin: "0 auto", padding: "20px", backgroundColor: "white" }}>
          {/* Route Partner Field */}
          <Stack sx={{ marginTop: 2 }}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography sx={{ fontSize: "13px", fontWeight: "600" }}>
                  Select Route Partner
                </Typography>
              </Box>
              <FormControl fullWidth margin="normal" size="small" error={Boolean(formik.touched.routePartner && formik.errors.routePartner)}>
                <Select
                  name="routePartner"
                  value={formik.values.routePartner.id}
                  onChange={e => {
                    const partner = routePartners.find((p) => p.id === e.target.value);
                    formik.setFieldValue('routePartner', { id: partner.id, name: partner.name });
                    formik.setFieldValue('fromCountry', partner?.country || '');
                  }}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="" disabled>Select Route Partner</MenuItem>
                  {routePartners.map((partner) => (
                    <MenuItem key={partner.id} value={partner.id}>{partner.name}</MenuItem>
                  ))}
                </Select>
                {formik.touched.routePartner && formik.errors.routePartner && <Typography color="error">{formik.errors.routePartner.id || formik.errors.routePartner.name}</Typography>}
              </FormControl>
            </Grid>
          </Stack>

          {/* From Country (read-only) */}
          <FormControl fullWidth margin="normal" size="small">
            <OutlinedInput
              name="fromCountry"
              value={formik.values.fromCountry}
              placeholder="From Country"
              disabled
            />
          </FormControl>

          {/* To Country */}
          <Stack sx={{ marginTop: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: "600" }}>
                    Select Country
                  </Typography>
                </Box>
                <FormControl fullWidth margin="normal" size="small">
                  <Select
                    name="toCountry"
                    value={formik.values.toCountry}
                    onChange={e => {
                      handleCountryChange(e);
                    }}
                    displayEmpty
                    fullWidth
                  >
                    <MenuItem value="" disabled>Select Country</MenuItem>
                    {COUNTRIES_WITH_STATES.map((country) => (
                      <MenuItem key={country.name} value={country.name}>{country.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <Box>
                  <Typography sx={{ fontSize: "13px", fontWeight: "600" }}>
                    Select State
                  </Typography>
                </Box>
                <FormControl fullWidth margin="normal" size="small" error={Boolean(formik.touched.toState && formik.errors.toState)}>
                  <Select
                    name="toState"
                    value={formik.values.toState}
                    onChange={formik.handleChange}
                    displayEmpty
                    fullWidth
                    disabled={!formik.values.toCountry}
                  >
                    <MenuItem value="" disabled>{formik.values.toCountry ? 'Select State' : 'Select Country First'}</MenuItem>
                    {(COUNTRIES_WITH_STATES.find(c => c.name === formik.values.toCountry)?.states || []).map((state) => (
                      <MenuItem key={state} value={state}>{state}</MenuItem>
                    ))}
                  </Select>
                  {formik.touched.toState && formik.errors.toState && <Typography color="error">{formik.errors.toState}</Typography>}
                </FormControl>
              </Grid>
            </Grid>
          </Stack>

          {/* Shipping Service Type */}
          <Stack sx={{ marginTop: 2 }}>
            <FormControl fullWidth margin="normal" size="small" error={Boolean(formik.touched.shippingServiceType && formik.errors.shippingServiceType)}>
              <FormLabel component="legend">Select the shipping service</FormLabel>
              <RadioGroup
                row
                name="shippingServiceType"
                value={formik.values.shippingServiceType}
                onChange={e => formik.setFieldValue('shippingServiceType', e.target.value)}
              >
                {shippingServiceOptions.map((option) => (
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    control={<Radio size="small" />}
                    label={option.label}
                  />
                ))}
              </RadioGroup>
              {formik.touched.shippingServiceType && formik.errors.shippingServiceType && <Typography color="error">{formik.errors.shippingServiceType}</Typography>}
            </FormControl>
          </Stack>

          {/* Transit Day Range */}
          <Stack sx={{ marginTop: 2 }}>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography sx={{ fontSize: "13px", fontWeight: "600" }}>Transit Day Range</Typography>
              </Box>
              <FormControl fullWidth margin="normal" size="small" error={Boolean(formik.touched.transitDayRange && formik.errors.transitDayRange)}>
                <Select
                  name="transitDayRange"
                  value={formik.values.transitDayRange}
                  onChange={formik.handleChange}
                  displayEmpty
                  fullWidth
                >
                  <MenuItem value="" disabled>Select Transit Day Range</MenuItem>
                  {transitDayOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                  ))}
                </Select>
                {formik.touched.transitDayRange && formik.errors.transitDayRange && <Typography color="error">{formik.errors.transitDayRange}</Typography>}
              </FormControl>
            </Grid>
          </Stack>

          {/* Select Shipping Warehouse (cards) */}
          <Stack sx={{ marginTop: 2 }}>
            <Box>
              <Typography sx={{ fontSize: "13px", fontWeight: "600" }}>
                Select Shipping Warehouse
              </Typography>
            </Box>
            <Grid container spacing={2}>
              {warehouses.map((location) => (
                <Grid item xs={12} sm={12} md={4} key={location.id}>
                  <LocationCard
                    location={location}
                    isSelected={formik.values.wareHouseDetails?.id === location.id}
                    onSelect={() => {
                      formik.setFieldValue('wareHouseDetails', { id: location.id, warehouseName: location.warehouseName });
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            {formik.touched.wareHouseDetails && formik.errors.wareHouseDetails && <Typography color="error">{formik.errors.wareHouseDetails}</Typography>}
          </Stack>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 6 }}>
            <Button
              onClick={onBack}
              type="button"
              sx={{
                background: '#F4F5F7',
                color: '#A0A4AF',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '16px',
                px: 5,
                py: 1.5,
                boxShadow: 'none',
                textTransform: 'none',
                '&:hover': {
                  background: '#E4E6EB',
                  color: '#A0A4AF',
                  boxShadow: 'none',
                },
              }}
              disabled
            >
              Back
            </Button>
            <Button
              type="submit"
              sx={{
                background: '#6C2BD7',
                color: '#fff',
                borderRadius: '8px',
                fontWeight: 700,
                fontSize: '16px',
                px: 5,
                py: 1.5,
                boxShadow: 'none',
                textTransform: 'none',
                '&:hover': {
                  background: '#5A23B7',
                  color: '#fff',
                  boxShadow: 'none',
                },
              }}
              onClick={formik.handleSubmit}
              disabled={formik.isSubmitting}
            >
              Continue
            </Button>
          </Box>
        </Box>
      </SimpleBar>
    </form>
  );
}
